// src/components/Branch/config/sections/StoreInfoSection.jsx
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  InformationCircleIcon,
  PencilIcon,
  CheckCircleIcon,
} from "@heroicons/react/outline";
import {
  updateStoreInfoThunk,
  updateFacilitiesThunk,
  updatePaymentMethodsThunk,
} from "../../../../store/thunks/branchDisplayThunks";

// Must match backend FACILITY_TYPES enum exactly
const FACILITIES = [
  // Parking
  { _id: "FREE_PARKING", name: "Free Parking" },
  { _id: "PAID_PARKING", name: "Paid Parking" },
  { _id: "VALET_PARKING", name: "Valet Parking" },
  { _id: "TWO_WHEELER_PARKING", name: "Two Wheeler Parking" },
  // Accessibility
  { _id: "WHEELCHAIR_ACCESS", name: "Wheelchair Access" },
  { _id: "ELEVATOR", name: "Elevator" },
  { _id: "GROUND_FLOOR", name: "Ground Floor" },
  // Comfort
  { _id: "AC_SHOWROOM", name: "AC Showroom" },
  { _id: "WIFI", name: "WiFi" },
  { _id: "REFRESHMENTS", name: "Refreshments" },
  { _id: "WAITING_LOUNGE", name: "Waiting Lounge" },
  { _id: "KIDS_PLAY_AREA", name: "Kids Play Area" },
  { _id: "PRAYER_ROOM", name: "Prayer Room" },
  { _id: "RESTROOM", name: "Restroom" },
  // Security
  { _id: "CCTV_SURVEILLANCE", name: "CCTV Surveillance" },
  { _id: "ARMED_SECURITY", name: "Armed Security" },
  { _id: "LOCKER_FACILITY", name: "Locker Facility" },
  { _id: "SAFE_DEPOSIT", name: "Safe Deposit" },
  // Shopping Experience
  { _id: "PRIVATE_VIEWING_ROOM", name: "Private Viewing Room" },
  { _id: "BRIDAL_LOUNGE", name: "Bridal Lounge" },
  { _id: "VIP_ROOM", name: "VIP Room" },
  { _id: "TRIAL_ROOM", name: "Trial Room" },
  { _id: "DESIGN_STUDIO", name: "Design Studio" },
  // Payment
  { _id: "ATM_ONSITE", name: "ATM Onsite" },
  { _id: "CARD_PAYMENT", name: "Card Payment" },
  { _id: "UPI_PAYMENT", name: "UPI Payment" },
  { _id: "EMI_COUNTER", name: "EMI Counter" },
  { _id: "FINANCE_DESK", name: "Finance Desk" },
  // Additional
  { _id: "GIFT_PACKING", name: "Gift Packing" },
  { _id: "ALTERATIONS_ONSITE", name: "Alterations Onsite" },
  { _id: "IN_HOUSE_WORKSHOP", name: "In-House Workshop" },
  { _id: "HALLMARKING_CENTER", name: "Hallmarking Center" },
];

// Must match backend PAYMENT_METHOD_TYPES enum exactly
const PAYMENT_METHODS = [
  { _id: "CASH", name: "Cash" },
  { _id: "CREDIT_CARD", name: "Credit Card" },
  { _id: "DEBIT_CARD", name: "Debit Card" },
  { _id: "UPI", name: "UPI" },
  { _id: "NET_BANKING", name: "Net Banking" },
  { _id: "EMI", name: "EMI" },
  { _id: "BAJAJ_FINSERV", name: "Bajaj Finserv" },
  { _id: "HDFC_EMI", name: "HDFC EMI" },
  { _id: "ICICI_EMI", name: "ICICI EMI" },
  { _id: "CHEQUE", name: "Cheque" },
  { _id: "DEMAND_DRAFT", name: "Demand Draft" },
  { _id: "RTGS", name: "RTGS" },
  { _id: "NEFT", name: "NEFT" },
  { _id: "IMPS", name: "IMPS" },
  { _id: "OLD_GOLD_EXCHANGE", name: "Old Gold Exchange" },
  { _id: "OLD_JEWELRY_EXCHANGE", name: "Old Jewelry Exchange" },
  { _id: "ADVANCE_PAYMENT", name: "Advance Payment" },
  { _id: "GOLD_SCHEME_REDEMPTION", name: "Gold Scheme Redemption" },
];

const STORE_SIZES = [
  { _id: "KIOSK", name: "Kiosk" },
  { _id: "BOUTIQUE", name: "Boutique" },
  { _id: "STANDARD", name: "Standard" },
  { _id: "LARGE", name: "Large" },
  { _id: "FLAGSHIP", name: "Flagship" },
  { _id: "MEGA_STORE", name: "Mega Store" },
];

const StoreInfoSection = ({ branchCode, isRootAdmin }) => {
  const dispatch = useDispatch();
  const { storeInfo = {}, loadingSection } = useSelector(
    (state) => state.branchDisplay
  );

  const [isEditingInfo, setIsEditingInfo] = useState(false);
  const [isEditingFacilities, setIsEditingFacilities] = useState(false);
  const [isEditingPayment, setIsEditingPayment] = useState(false);

  const [infoForm, setInfoForm] = useState({
    establishedYear: "",
    storeSize: "",
    numberOfFloors: 1,
    floorArea: { value: "", unit: "SQFT" },
  });
  const [selectedFacilities, setSelectedFacilities] = useState([]);
  const [selectedPayments, setSelectedPayments] = useState([]);

  useEffect(() => {
    if (storeInfo && Object.keys(storeInfo).length > 0) {
      setInfoForm({
        establishedYear: storeInfo.establishedYear || "",
        storeSize: storeInfo.storeSize || "",
        numberOfFloors: storeInfo.numberOfFloors || 1,
        floorArea: {
          value: storeInfo.floorArea?.value || "",
          unit: storeInfo.floorArea?.unit || "SQFT",
        },
      });
      setSelectedFacilities(
        Array.isArray(storeInfo.facilities) ? storeInfo.facilities : []
      );
      setSelectedPayments(
        Array.isArray(storeInfo.paymentMethods) ? storeInfo.paymentMethods : []
      );
    }
  }, [storeInfo]);

  const handleSaveInfo = async () => {
    await dispatch(updateStoreInfoThunk({ branchCode, storeInfo: infoForm }));
    setIsEditingInfo(false);
  };

  const handleSaveFacilities = async () => {
    await dispatch(
      updateFacilitiesThunk({ branchCode, facilities: selectedFacilities })
    );
    setIsEditingFacilities(false);
  };

  const handleSavePayments = async () => {
    await dispatch(
      updatePaymentMethodsThunk({
        branchCode,
        paymentMethods: selectedPayments,
      })
    );
    setIsEditingPayment(false);
  };

  const toggleFacility = (facilityId) => {
    setSelectedFacilities((prev) =>
      prev.includes(facilityId)
        ? prev.filter((f) => f !== facilityId)
        : [...prev, facilityId]
    );
  };

  const togglePayment = (paymentId) => {
    setSelectedPayments((prev) =>
      prev.includes(paymentId)
        ? prev.filter((p) => p !== paymentId)
        : [...prev, paymentId]
    );
  };

  const getFacilityName = (id) => {
    const facility = FACILITIES.find((f) => f._id === id);
    return facility ? facility.name : id.replace(/_/g, " ").toLowerCase();
  };

  const getPaymentName = (id) => {
    const payment = PAYMENT_METHODS.find((p) => p._id === id);
    return payment ? payment.name : id.replace(/_/g, " ").toLowerCase();
  };

  return (
    <div className="space-y-6">
      {/* Store Info */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <div className="flex justify-between items-center mb-4">
          <h4 className="font-medium flex items-center">
            <InformationCircleIcon className="h-4 w-4 mr-2" /> Store Information
          </h4>
          {isRootAdmin && !isEditingInfo && (
            <button
              onClick={() => setIsEditingInfo(true)}
              className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
            >
              <PencilIcon className="h-4 w-4" /> Edit
            </button>
          )}
        </div>
        {isEditingInfo ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Established Year
                </label>
                <input
                  type="number"
                  value={infoForm.establishedYear}
                  onChange={(e) =>
                    setInfoForm((p) => ({
                      ...p,
                      establishedYear: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="1990"
                  min="1800"
                  max={new Date().getFullYear()}
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Store Size
                </label>
                <select
                  value={infoForm.storeSize}
                  onChange={(e) =>
                    setInfoForm((p) => ({ ...p, storeSize: e.target.value }))
                  }
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="">Select size</option>
                  {STORE_SIZES.map((size) => (
                    <option key={size._id} value={size._id}>
                      {size.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Floor Area
                </label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    value={infoForm.floorArea.value}
                    onChange={(e) =>
                      setInfoForm((p) => ({
                        ...p,
                        floorArea: { ...p.floorArea, value: e.target.value },
                      }))
                    }
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="2000"
                  />
                  <select
                    value={infoForm.floorArea.unit}
                    onChange={(e) =>
                      setInfoForm((p) => ({
                        ...p,
                        floorArea: { ...p.floorArea, unit: e.target.value },
                      }))
                    }
                    className="px-3 py-2 border rounded-lg"
                  >
                    <option value="SQFT">sq ft</option>
                    <option value="SQM">sq m</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Number of Floors
                </label>
                <input
                  type="number"
                  value={infoForm.numberOfFloors}
                  onChange={(e) =>
                    setInfoForm((p) => ({
                      ...p,
                      numberOfFloors: parseInt(e.target.value) || 1,
                    }))
                  }
                  className="w-full px-3 py-2 border rounded-lg"
                  min="1"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleSaveInfo}
                disabled={loadingSection === "storeInfo"}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 flex items-center gap-2"
              >
                <CheckCircleIcon className="h-4 w-4" /> Save
              </button>
              <button
                onClick={() => setIsEditingInfo(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <span className="text-sm text-gray-600">Established:</span>
              <p className="font-medium">
                {storeInfo?.establishedYear || "Not set"}
              </p>
            </div>
            <div>
              <span className="text-sm text-gray-600">Store Size:</span>
              <p className="font-medium">
                {storeInfo?.storeSize
                  ? STORE_SIZES.find((s) => s._id === storeInfo.storeSize)
                      ?.name || storeInfo.storeSize
                  : "Not set"}
              </p>
            </div>
            <div>
              <span className="text-sm text-gray-600">Floor Area:</span>
              <p className="font-medium">
                {storeInfo?.floorArea?.value
                  ? `${storeInfo.floorArea.value} ${
                      storeInfo.floorArea.unit === "SQM" ? "sq m" : "sq ft"
                    }`
                  : "Not set"}
              </p>
            </div>
            <div>
              <span className="text-sm text-gray-600">Floors:</span>
              <p className="font-medium">{storeInfo?.numberOfFloors || 1}</p>
            </div>
          </div>
        )}
      </div>

      {/* Facilities */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <div className="flex justify-between items-center mb-4">
          <h4 className="font-medium">Facilities & Amenities</h4>
          {isRootAdmin && !isEditingFacilities && (
            <button
              onClick={() => setIsEditingFacilities(true)}
              className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
            >
              <PencilIcon className="h-4 w-4" /> Edit
            </button>
          )}
        </div>
        {isEditingFacilities ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {FACILITIES.map((facility) => (
                <label
                  key={facility._id}
                  className={`flex items-center gap-2 p-2 rounded cursor-pointer border ${
                    selectedFacilities.includes(facility._id)
                      ? "bg-blue-50 border-blue-300"
                      : "bg-white border-gray-200"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedFacilities.includes(facility._id)}
                    onChange={() => toggleFacility(facility._id)}
                    className="h-4 w-4"
                  />
                  <span className="text-sm">{facility.name}</span>
                </label>
              ))}
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleSaveFacilities}
                disabled={loadingSection === "facilities"}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 flex items-center gap-2"
              >
                <CheckCircleIcon className="h-4 w-4" /> Save
              </button>
              <button
                onClick={() => setIsEditingFacilities(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {selectedFacilities.length === 0 ? (
              <p className="text-gray-500">No facilities listed</p>
            ) : (
              selectedFacilities.map((facilityId, i) => (
                <span
                  key={i}
                  className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm"
                >
                  {getFacilityName(facilityId)}
                </span>
              ))
            )}
          </div>
        )}
      </div>

      {/* Payment Methods */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <div className="flex justify-between items-center mb-4">
          <h4 className="font-medium">Payment Methods</h4>
          {isRootAdmin && !isEditingPayment && (
            <button
              onClick={() => setIsEditingPayment(true)}
              className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
            >
              <PencilIcon className="h-4 w-4" /> Edit
            </button>
          )}
        </div>
        {isEditingPayment ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {PAYMENT_METHODS.map((payment) => (
                <label
                  key={payment._id}
                  className={`flex items-center gap-2 p-2 rounded cursor-pointer border ${
                    selectedPayments.includes(payment._id)
                      ? "bg-blue-50 border-blue-300"
                      : "bg-white border-gray-200"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedPayments.includes(payment._id)}
                    onChange={() => togglePayment(payment._id)}
                    className="h-4 w-4"
                  />
                  <span className="text-sm">{payment.name}</span>
                </label>
              ))}
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleSavePayments}
                disabled={loadingSection === "paymentMethods"}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 flex items-center gap-2"
              >
                <CheckCircleIcon className="h-4 w-4" /> Save
              </button>
              <button
                onClick={() => setIsEditingPayment(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {selectedPayments.length === 0 ? (
              <p className="text-gray-500">No payment methods listed</p>
            ) : (
              selectedPayments.map((paymentId, i) => (
                <span
                  key={i}
                  className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm"
                >
                  {getPaymentName(paymentId)}
                </span>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default StoreInfoSection;
